import buildUrl from 'api-services/utils/buildUrl';
import React from 'react';
import genClientAuthCodes from '../genClientCodes';
import * as HeroIamApi from 'api-services/HeroIamApi';
import { getRefreshToken, removeRefreshToken, setRefreshToken } from '../refreshToken';
const AuthContext = React.createContext(undefined);
export const useAuthContext = () => {
    const ctx = React.useContext(AuthContext);
    if (ctx === undefined) {
        throw new Error(`'useAuthContext' must be used within a 'AuthContextProvider'`);
    }
    return ctx;
};
const authCodesKey = 'authCodes';
const AuthProvider = ({ children, constAccessParams }) => {
    const [auth, setAuth] = React.useState();
    const [authLoading, setAuthLoading] = React.useState(false);
    const [logoutLoading, setLogoutLoading] = React.useState(false);
    const rawAuthStatus = localStorage.getItem('authStatus');
    const authStatus = typeof rawAuthStatus === 'string' ? JSON.parse(rawAuthStatus) : undefined;
    const setAuthStatus = React.useCallback((status) => {
        localStorage.setItem('authStatus', JSON.stringify(status));
    }, []);
    const initLogin = React.useCallback(async () => {
        const authCodes = await genClientAuthCodes();
        localStorage.setItem(authCodesKey, JSON.stringify(authCodes));
        setAuth(undefined);
        removeRefreshToken();
        const queryParams = {
            response_type: 'code',
            client_id: constAccessParams.API_ID_CLIENT_ID,
            redirect_uri: window.location.origin,
            code_challenge: authCodes?.code_challenge || '',
            state: authCodes?.state || ''
        };
        const url = buildUrl(constAccessParams.API_ID_HOSTNAME + 'login/', queryParams);
        window.location.assign(url);
        setAuthStatus('logging');
    }, [constAccessParams, setAuthStatus]);
    const obtainToken = React.useCallback(() => {
        const urlQueryParams = new URLSearchParams(window?.location?.search);
        const code = urlQueryParams.get('code');
        const state = urlQueryParams.get('state');
        const rawAuthCodes = localStorage.getItem('authCodes');
        const authCodes = JSON.parse(rawAuthCodes);
        const queryParams = {
            grant_type: 'authorization_code',
            client_id: constAccessParams.API_ID_CLIENT_ID,
            code_verifier: authCodes?.code_verifier || '',
            redirect_uri: window?.location?.origin || '',
            code,
            state
        };
        if (queryParams?.state !== authCodes?.state) {
            initLogin();
        }
        else {
            setAuthLoading(true);
            HeroIamApi.obtainToken(constAccessParams.API_ID_HOSTNAME)(queryParams)
                .then((result) => {
                if (result.success) {
                    const newAuth = result.data;
                    setAuth(newAuth);
                    setRefreshToken(newAuth.refresh_token);
                    setAuthStatus('loggedIn');
                    localStorage.removeItem(authCodesKey);
                }
                else {
                    initLogin();
                }
            })
                .catch((e) => {
                console.error(e);
                initLogin();
            })
                .finally(() => setAuthLoading(false));
        }
    }, [constAccessParams, initLogin, setAuthStatus]);
    const makeAuthCall = React.useCallback(() => {
        if (!authStatus || authStatus === 'logout') {
            initLogin();
        }
        !auth && !authLoading && !logoutLoading && authStatus === 'logging' && obtainToken();
    }, [auth, obtainToken, authLoading, authStatus, initLogin]);
    const logout = React.useCallback(() => {
        setLogoutLoading(true);
        setAuthStatus('logout');
        HeroIamApi.logout(constAccessParams.API_ID_HOSTNAME, {
            client_id: constAccessParams.API_ID_CLIENT_ID,
            access_token: auth?.access_token || ''
        })
            .then(async () => {
            await initLogin();
            setLogoutLoading(false);
        })
            .catch(async (e) => {
            console.error(e);
            await initLogin();
            setLogoutLoading(false);
        });
    }, [initLogin, constAccessParams, auth]);
    const refreshToken = React.useCallback(async () => {
        const refreshTokenValue = getRefreshToken();
        try {
            const response = await HeroIamApi.refreshToken(constAccessParams.API_ID_HOSTNAME, constAccessParams.API_ID_CLIENT_ID, refreshTokenValue);
            if (response.success) {
                setAuth(response.data);
                setRefreshToken(response.data.refresh_token);
                setAuthStatus('loggedIn');
            }
            else {
                logout();
            }
            return response;
        }
        catch (e) {
            console.error(e);
            logout();
            return Promise.reject('Failed');
        }
    }, [constAccessParams]);
    React.useEffect(() => {
        authStatus === 'loggedIn' && refreshToken();
    }, [authStatus, refreshToken]);
    return (React.createElement(AuthContext.Provider, { value: {
            logout,
            makeAuthCall,
            auth,
            initLogin,
            isLoading: authLoading || logoutLoading,
            refreshToken
        } }, children));
};
export default AuthProvider;
//# sourceMappingURL=AuthProvider.js.map